/**
 * Client-side battalion rotation algorithm.
 *
 * Mirrors the backend algorithm in src/worker/schedule.ts (SDD §6.1–6.3)
 * so the UI always shows the correct on-duty battalion even when the
 * pre-generated database schedule is stale or missing.
 *
 * Anchor: Battalion 4 on Wed 2025-02-12 (migrations/0020_fix_schedule_anchor.sql)
 */

const ANCHOR_DATE = '2025-02-12';
const ANCHOR_BATTALION = 4;

export type ShiftType = 'weeknight' | 'friday' | 'weekend';

export interface ClientDutyAssignment {
  battalion: number;
  shiftType: ShiftType;
  isSunday: boolean;
}

/**
 * Compute the on-duty battalion for any date using the 5-battalion
 * forward rotation algorithm. Returns the battalion number (1–5)
 * and shift type. Sundays return the Saturday battalion with isSunday=true.
 */
export function computeBattalionForDate(dateStr: string): ClientDutyAssignment {
  const target = new Date(dateStr + 'T00:00:00Z');
  const anchor = new Date(ANCHOR_DATE + 'T00:00:00Z');
  const targetDay = target.getUTCDay(); // 0=Sun

  // Sunday: compute Saturday's battalion
  if (targetDay === 0) {
    const sat = new Date(target.getTime() - 24 * 60 * 60 * 1000);
    const satStr = formatDateYMD(sat);
    const satResult = computeBattalionForDate(satStr);
    return { ...satResult, isSunday: true };
  }

  const anchorDay = anchor.getUTCDay();

  const toMondayIndex = (day: number): number => (day === 0 ? 6 : day - 1);

  const targetMondayIdx = toMondayIndex(targetDay);
  const anchorMondayIdx = toMondayIndex(anchorDay);

  const targetMonday = new Date(target.getTime() - targetMondayIdx * 24 * 60 * 60 * 1000);
  const anchorMonday = new Date(anchor.getTime() - anchorMondayIdx * 24 * 60 * 60 * 1000);
  const weeksDiff = Math.round((targetMonday.getTime() - anchorMonday.getTime()) / (7 * 24 * 60 * 60 * 1000));

  const slotDiff = targetMondayIdx - anchorMondayIdx;
  const offset = slotDiff + weeksDiff;
  const battalion = ((((ANCHOR_BATTALION - 1 + offset) % 5) + 5) % 5) + 1;

  let shiftType: ShiftType;
  if (targetDay >= 1 && targetDay <= 4) {
    shiftType = 'weeknight';
  } else if (targetDay === 5) {
    shiftType = 'friday';
  } else {
    shiftType = 'weekend';
  }

  return { battalion, shiftType, isSunday: false };
}

/**
 * Get today's date as YYYY-MM-DD in local time.
 */
export function getTodayDateStr(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function formatDateYMD(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
